import { memo, useMemo } from 'react';

import { chartThemeTokens } from 'design-system';

import type {
  ChartResultRow,
  ForwardChartWidgetData,
  SeasonalChartWidgetData,
  SpotChartWidgetData
} from '@/features/_shared/widget-registry';
import type { MarketApps } from '@/features/dashboard';
import type {
  FWDChartWidgetV2,
  SeasonalChartWidgetV2,
  SimpleChartWidgetV2
} from '@/features/dashboard/types';
import { useMarketPreview } from '@/features/markets/api';
import type { FWDTemplateChartSeries, TemplateChartSeries } from '@/shared/types/charts';

import { MarketPreviewShell } from './MarketPreviewWidget';

type ChartPreviewWidgetProps =
  | { data: SpotChartWidgetData; chartType: 'spot' }
  | { data: SeasonalChartWidgetData; chartType: 'seasonal' }
  | { data: ForwardChartWidgetData; chartType: 'forward' };

const DEFAULT_SYMBOL_GROUP = 'Prod_Indexes';
const PERSISTED_VIBRANT_COLOR_NAMES = [
  'Blue',
  'Green',
  'Yellow',
  'Purple',
  'Orange',
  'Pink'
] as const;

function buildLineSettings(index: number): TemplateChartSeries['settings'] {
  const paletteIndex = index % PERSISTED_VIBRANT_COLOR_NAMES.length;

  return {
    type: 'line',
    color: chartThemeTokens.seriesPalettes.vibrant[paletteIndex],
    colorName: PERSISTED_VIBRANT_COLOR_NAMES[paletteIndex]
  };
}

function buildForwardChartSymbol(row: ChartResultRow): FWDTemplateChartSeries['symbol'] {
  return {
    key: {
      Code: row.code,
      PeriodType: row.period_type,
      TimeRef: String(row.time_ref ?? '')
    },
    group: row.group ?? DEFAULT_SYMBOL_GROUP,
    columns: null
  };
}

function buildTemplateChartSymbol(row: ChartResultRow): TemplateChartSeries['symbol'] {
  const symbol = buildForwardChartSymbol(row);

  return {
    ...symbol,
    key: {
      ...symbol.key,
      Period: row.period ?? '1'
    }
  };
}

function buildChartSeries(row: ChartResultRow, index: number): TemplateChartSeries {
  return {
    settings: buildLineSettings(index),
    shortTitle: row.title,
    symbol: buildTemplateChartSymbol(row)
  };
}

function buildForwardChartSeries(row: ChartResultRow, index: number): FWDTemplateChartSeries {
  return {
    settings: buildLineSettings(index),
    shortTitle: row.title,
    symbol: buildForwardChartSymbol(row)
  };
}

function buildSpotTemplate(data: SpotChartWidgetData): MarketApps {
  const tab: SimpleChartWidgetV2 = {
    id: `ai-chat-spot-tab-${data.tool_call_id}`,
    name: data.title ?? 'Spot History chart',
    type: 'graph',
    metadata: null,
    series: data.rows.map((row, index) => buildChartSeries(row, index))
  };
  return [{ id: `ai-chat-spot-${data.tool_call_id}`, tabs: [tab] }];
}

// Normalise the `from` value to a valid duration string understood by the
// seasonal chart. The API can send a bare integer (e.g. "2") meaning 2 years,
// or an explicit duration string (e.g. "2y", "6m"). We append "y" only when
// the value is a plain integer to avoid corrupting durations like "6m".
const normalizeSeasonalFrom = (from: string): string => (/^\d+$/.test(from) ? `${from}y` : from);

function buildSeasonalTemplate(data: SeasonalChartWidgetData): MarketApps {
  const from = normalizeSeasonalFrom(data.from ?? '2y');
  const tab: SeasonalChartWidgetV2 = {
    id: `ai-chat-seasonal-tab-${data.tool_call_id}`,
    name: data.title ?? 'Seasonal chart',
    type: 'graph',
    metadata: { graphType: 'seasonal', from },
    series: data.rows.map((row, index) => buildChartSeries(row, index))
  };
  return [{ id: `ai-chat-seasonal-${data.tool_call_id}`, tabs: [tab] }];
}

function buildForwardTemplate(data: ForwardChartWidgetData): MarketApps {
  const tab: FWDChartWidgetV2 = {
    id: `ai-chat-fwd-tab-${data.tool_call_id}`,
    name: data.title ?? 'Forward Curves chart',
    type: 'graph',
    metadata: { graphType: 'forwardSimple' },
    series: data.rows.map((row, index) => buildForwardChartSeries(row, index))
  };
  return [{ id: `ai-chat-fwd-${data.tool_call_id}`, tabs: [tab] }];
}

export const ChartPreviewWidget = memo((props: ChartPreviewWidgetProps) => {
  const template = useMemo<MarketApps>(() => {
    if (props.chartType === 'spot') return buildSpotTemplate(props.data);
    if (props.chartType === 'seasonal') return buildSeasonalTemplate(props.data);
    return buildForwardTemplate(props.data);
  }, [props.chartType, props.data]);

  const { data: previewData, isError, isLoading } = useMarketPreview(template);

  return (
    <MarketPreviewShell
      previewData={previewData}
      isError={isError}
      isLoading={isLoading}
    />
  );
});

ChartPreviewWidget.displayName = 'ChartPreviewWidget';
